import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment, memo } from "react";
import { Handle, Position, useNodeId } from "@xyflow/react";
import { Plus, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { OVERLAY_COLOR_HEX, getAssigneeName } from "../utils";
import { NodeOverlayLabel } from "./NodeOverlayLabel";
import { groupRulesByOutput } from "../../../decision-table/decisionTableColumns";
import { buildRuleHandleId } from "../edgeClassifier";
import { useIsContextMenuActiveNode, useIsDropTarget } from "../../../flow-kit";
import { useNodeVisualState } from "../hooks/useNodeVisualState";
import { HandleErrorDot } from "./HandleErrorDot";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../../../base/tooltip/tooltip";
const stepLabelStyle = {
    top: 62,
    fontSize: 10,
    lineHeight: 1.2,
    width: 140,
    wordBreak: "break-word"
};
// Inner diamond path for a 60x60 viewBox — vertices at the edge midpoints.
const INNER_DIAMOND_PATH = "M 30 0 L 60 30 L 30 60 L 0 30 Z";
// Outer selection-ring diamond for a 68x68 viewBox — scaled so it matches
// ActivityNode's `ring-offset-2 ring-2` geometry once positioned at (-4,-4).
const OUTER_DIAMOND_PATH = "M 34 0 L 68 34 L 34 68 L 0 34 Z";
function ProcessFlowGatewayNode({ data }) {
    const { t } = useTranslation();
    const nodeId = useNodeId();
    const isDropTarget = useIsDropTarget(nodeId);
    const isContextMenuActive = useIsContextMenuActiveNode(nodeId);
    const isEdit = data?.mode === "edit";
    // The synthetic "+" gateway is injected by builder.ts to aggregate multiple
    // outbound paths from a task — it has no backing step recording. Its handles
    // must never initiate a connection (it's an aggregator, not a real step).
    const isSyntheticPlus = data?.type === "parallel" && !data?.stepRecording;
    const handlesConnectable = isEdit && !isSyntheticPlus;
    const { state, tokens, isDashed, dimClass, handleMouseEnter, handleMouseLeave } = useNodeVisualState({
        isSelected: data?.isSelected,
        isContextMenuActive,
        isCurrentStep: data?.isCurrentStep,
        isOnPath: data?.isOnPath,
        isDimmed: data?.isDimmed,
        isEdit,
        hasPublishError: data?.hasPublishError,
        isOrphaned: data?.isOrphaned,
        isSkeleton: data?.isSkeleton
    });
    if (!data) return null;
    const isDecision = data.stepRecording?.actionType === "decision";
    // Only decisions render a label (see `showLabel`), and a decision's `task`
    // bucket is leftover text kept across a toggle — never its name.
    const stepName = data.stepRecording?.generatedName || "";
    const allRules = data.stepRecording?.rules ?? [];
    const grouped = isDecision ? groupRulesByOutput(allRules) : null;
    const outputGroups = grouped?.groups ?? [];
    const incompleteRuleIds = data.incompleteRuleIds;
    // A handle is "incomplete" only if every rule sharing its output is
    // incomplete — a single wired-up sibling keeps the handle visually clean.
    // Start with every handle marked and rule out any rep that has a complete
    // sibling; cheaper than rebuilding rules-by-output since `groupRulesByOutput`
    // already produced the rule→rep map.
    const incompleteOutputs = new Set();
    if (grouped && incompleteRuleIds && incompleteRuleIds.size > 0) {
        for (const group of outputGroups)incompleteOutputs.add(group.firstRuleId);
        for (const [ruleId, rep] of grouped.ruleIdToRepresentative){
            if (!incompleteRuleIds.has(ruleId)) incompleteOutputs.delete(rep);
        }
    }
    // Overlay (fill + stroke) only applies when no state token has claimed the
    // diamond, so highlights still show on unfocused nodes.
    const overlay = state === "default" && data.overlayColor ? OVERLAY_COLOR_HEX[data.overlayColor] : null;
    const fill = overlay?.fill ?? tokens.fill;
    const stroke = overlay?.stroke ?? tokens.stroke;
    // SVG equivalent of the activity card's ring-offset-2 outline.
    const showOuterAccent = state === "hover" || state === "active" || isDropTarget;
    const outerStroke = state === "active" || isDropTarget ? "#3B82F6" : "#60A5FA";
    const showLabel = isDecision && (isEdit || !!stepName);
    // Node dimensions must match layout: width=60 (GATEWAY_SIZE), height=90 (GATEWAY_NODE_HEIGHT).
    // Handles connect at the diamond center (30px from top).
    //
    // Custom SVG paths are used instead of lucide's Diamond because lucide's
    // strokeWidth scales with size (strokeWidth=2 on size=60 renders as ~5px).
    // With two scaled strokes and a small gap between them, the inner and outer
    // strokes overlap into one solid mass. Using paths in a 60/68-unit viewBox
    // keeps strokeWidth in real pixels, preserving the 2px inner + 2px gap +
    // 2px outer geometry that matches ActivityNode's `border-2 ring-offset-2 ring-2`.
    return /*#__PURE__*/ _jsxs("div", {
        className: `relative transition-opacity duration-200 ${dimClass} ${tokens.textClass}`,
        style: {
            width: 60,
            height: 90,
            zIndex: 1
        },
        onMouseEnter: handleMouseEnter,
        onMouseLeave: handleMouseLeave,
        children: [
            /*#__PURE__*/ _jsx(Handle, {
                type: "target",
                position: Position.Left,
                style: {
                    top: 30
                },
                isConnectable: handlesConnectable
            }),
            /*#__PURE__*/ _jsx("svg", {
                width: 60,
                height: 60,
                viewBox: "0 0 60 60",
                className: "block",
                children: /*#__PURE__*/ _jsx("path", {
                    d: INNER_DIAMOND_PATH,
                    fill: fill,
                    stroke: stroke,
                    strokeWidth: isDashed ? 1.5 : 2,
                    strokeLinejoin: "round",
                    strokeDasharray: isDashed ? "4 4" : undefined
                })
            }),
            data.stepRecording?.stepNumber != null && /*#__PURE__*/ _jsx("div", {
                className: "absolute -top-2 -left-2 z-[2] min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-fg-secondary text-white text-3xs font-semibold leading-none ring-2 ring-white pointer-events-none",
                "aria-hidden": true,
                children: data.stepRecording.stepNumber
            }),
            showOuterAccent && /*#__PURE__*/ _jsx("svg", {
                width: 68,
                height: 68,
                viewBox: "0 0 68 68",
                className: "absolute pointer-events-none drop-shadow",
                style: {
                    top: -4,
                    left: -4
                },
                children: /*#__PURE__*/ _jsx("path", {
                    d: OUTER_DIAMOND_PATH,
                    fill: "none",
                    stroke: outerStroke,
                    strokeWidth: 2,
                    strokeLinejoin: "round"
                })
            }),
            isDecision && /*#__PURE__*/ _jsx(X, {
                size: 20,
                strokeWidth: 2.5,
                className: "absolute left-1/2 pointer-events-none text-muted-foreground",
                style: {
                    top: 30,
                    transform: "translate(-50%, -50%)"
                }
            }),
            !isDecision && data.type === "parallel" && /*#__PURE__*/ _jsx(Plus, {
                size: 22,
                strokeWidth: 2.5,
                className: "absolute left-1/2 text-muted-foreground pointer-events-none",
                style: {
                    top: 30,
                    transform: "translate(-50%, -50%)"
                }
            }),
            showLabel && /*#__PURE__*/ _jsx("div", {
                className: `absolute left-1/2 -translate-x-1/2 text-center font-medium pointer-events-none ${stepName ? "text-fg-secondary" : "text-fg-subtle italic"}`,
                style: stepLabelStyle,
                children: stepName ? stepName.length > 60 ? `${stepName.slice(0, 60)}...` : stepName : (()=>{
                    const label = t("processFlowEditor.newDecision");
                    const assignee = data.stepRecording ? getAssigneeName(data.stepRecording) : "";
                    return assignee ? `${label} — ${assignee}` : label;
                })()
            }),
            isDecision && /*#__PURE__*/ _jsx(TooltipProvider, {
                delayDuration: 150,
                children: outputGroups.map(({ firstRuleId, output }, index)=>{
                    const n = outputGroups.length;
                    const top = n === 1 ? 30 : 20 + index * 20 / (n - 1);
                    const isIncomplete = incompleteOutputs.has(firstRuleId);
                    const label = output || t("processFlowEditor.ruleNoOutput");
                    return /*#__PURE__*/ _jsxs(Fragment, {
                        children: [
                            /*#__PURE__*/ _jsxs(Tooltip, {
                                children: [
                                    /*#__PURE__*/ _jsx(TooltipTrigger, {
                                        asChild: true,
                                        children: /*#__PURE__*/ _jsx(Handle, {
                                            type: "source",
                                            position: Position.Right,
                                            id: buildRuleHandleId(firstRuleId),
                                            style: {
                                                top
                                            },
                                            isConnectable: handlesConnectable
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(TooltipContent, {
                                        side: "right",
                                        children: label
                                    })
                                ]
                            }),
                            isIncomplete && /*#__PURE__*/ _jsx(HandleErrorDot, {
                                top: top,
                                zIndex: 2,
                                label: label
                            })
                        ]
                    }, firstRuleId);
                })
            }),
            !isDecision && /*#__PURE__*/ _jsx(Handle, {
                type: "source",
                position: Position.Right,
                style: {
                    top: 30
                },
                isConnectable: handlesConnectable
            }),
            /*#__PURE__*/ _jsx(NodeOverlayLabel, {
                color: data.overlayColor,
                label: data.overlayLabel,
                badges: data.overlayBadges,
                // Centred above the top vertex: the rule edges leave the right vertex and
                // the incoming edge enters the left one, so a row there covers neither,
                // and it sits higher than the step-number chip at the top-left corner.
                anchor: {
                    top: -26,
                    left: 30,
                    transform: "translateX(-50%)"
                }
            })
        ]
    });
}
export default /*#__PURE__*/ memo(ProcessFlowGatewayNode);

//# sourceMappingURL=GatewayNode.js.map